/*
 * Shared connector-error plumbing for Deckorate's AI image features (Sketch
 * to Slide, Photo Headshot, ...). Each feature keeps its own error-code
 * tables, message copy, and named logger export — only the generic
 * HTTP-error parsing and telemetry-logging mechanics live here.
 */

import { logError, scrubMessage } from "../../../../../telemetry";

export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/** Builds a feature's `logXError(reason, message)` telemetry logger. */
export function createConnectorLogger(
  connectorSystemName: string,
  errorType: string,
): (reason: string, message: string) => void {
  return (reason, message) => {
    logError(
      "error.connector.failure",
      { type: errorType, scrubbedMessage: scrubMessage(message) },
      { connectorSystemName, reason },
    );
  };
}

/* ── Error copy — createHttpClient formats failures as
 * `Deckorate <METHOD> <path>: <status> <body>`; the parsing convention below
 * pulls the status/body back out of that string for status-based copy. */

const HTTP_ERROR_PREFIX = "Deckorate ";
const HTTP_ERROR_SEPARATOR = ": ";
const LEADING_STATUS_PATTERN = /^\d{3}/;

export interface ParsedHttpError {
  readonly status: number;
  readonly body: string;
}

export function parseHttpError(raw: string): ParsedHttpError | null {
  if (!raw.startsWith(HTTP_ERROR_PREFIX)) return null;
  const separator = raw.indexOf(HTTP_ERROR_SEPARATOR, HTTP_ERROR_PREFIX.length);
  if (separator === -1) return null;
  const remainder = raw.slice(separator + HTTP_ERROR_SEPARATOR.length);
  const status = LEADING_STATUS_PATTERN.exec(remainder);
  if (!status) return null;
  return { status: Number(status[0]), body: remainder.slice(status[0].length).trim() };
}

export const HTTP_STATUS = { unauthorized: 401, forbidden: 403, serverError: 500, badGateway: 502 } as const;

/** Fallback message logged (not shown to the user) when an error body isn't parseable JSON. */
const NON_JSON_ERROR_BODY_FALLBACK = "Non-JSON error body";

/**
 * Parses an HTTP error body as JSON and hands the result to `extract` for the
 * caller's own envelope shape (photoHeadApi.ts's `{message}` / `{error:{message}}`,
 * sketchToSlideApi.ts's same shape plus an `error.code`). Non-JSON bodies and
 * non-object JSON both resolve to `null`; a parse failure is logged via
 * `onParseError` before resolving to `null` too, rather than throwing.
 */
export function parseJsonErrorBody<T>(
  rawBody: string,
  extract: (parsed: Record<string, unknown>) => T | null,
  onParseError: (reason: string, message: string) => void,
  parseErrorReason: string,
): T | null {
  try {
    const parsed: unknown = JSON.parse(rawBody);
    if (typeof parsed !== "object" || parsed === null) return null;
    return extract(parsed as Record<string, unknown>);
  } catch (error) {
    onParseError(parseErrorReason, getErrorMessage(error, NON_JSON_ERROR_BODY_FALLBACK));
    return null;
  }
}
